import { useMutation } from "@tanstack/react-query";

import { request } from "@/lib/request";
import { Template, TemplateVisibility } from "@/lib/types";
import { useUser } from "../useUserProvider";

export const useUpdateTemplate = () => {
  const { accessToken } = useUser();

  return useMutation({
    mutationKey: ["updateTemplate"],
    mutationFn: async ({
      templateId,
      name,
      description,
      categoryId,
      subcategoryId,
      visibility,
      teamId,
      tags,
      thumbnailFileIds,
      demoVideoUrl,
    }: {
      templateId: string;
      name?: string;
      description?: string;
      categoryId?: string;
      subcategoryId?: string;
      visibility?: TemplateVisibility;
      teamId?: string;
      tags?: string[];
      thumbnailFileIds?: string[];
      demoVideoUrl?: string;
    }): Promise<Template> => {
      if (!accessToken) {
        throw new Error("You need to be logged in to edit templates.");
      }

      const payload: Record<string, unknown> = {};

      if (name !== undefined) {
        payload.name = name.trim();
      }

      if (description !== undefined) {
        payload.description = description.trim();
      }

      if (categoryId !== undefined) {
        payload.categoryId = categoryId;
      }

      if (subcategoryId !== undefined) {
        payload.subcategoryId = subcategoryId;
      }

      if (visibility !== undefined) {
        payload.visibility = visibility;
      }

      if (teamId !== undefined) {
        payload.teamId = teamId;
      }

      if (tags !== undefined) {
        payload.tags = tags;
      }

      if (thumbnailFileIds !== undefined) {
        payload.thumbnailFileIds = thumbnailFileIds;
      }

      if (demoVideoUrl !== undefined) {
        payload.demoVideoUrl = demoVideoUrl.trim();
      }

      const res = await request
        .put<{ data: Template }>(`/templates/${templateId}`, payload)
        .catch(err => {
          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : "Failed to update template.",
          );
        });

      return res.data.data;
    },
  });
};
